'use client';

import { useEffect, useState } from 'react';
import { useLocale } from 'hooks/useLocale';
import {
  getLegalPage,
  legalLocaleFor,
  type LegalContentLocale,
  type LegalSlug,
  type LegalVariants,
} from 'core/legal';
import { LegalPageLayout } from './LegalPageLayout';
import { LegalUnavailable } from './LegalUnavailable';

type Props = {
  slug: LegalSlug;
  /** All published variants, compiled at build time in getStaticProps. */
  variants: LegalVariants;
};

export const LegalPage = ({ slug, variants }: Props) => {
  const { locale } = useLocale();
  // First paint (static HTML + pre-hydration) renders `en` so server and
  // client agree; after mount we adopt the visitor's resolved locale.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const contentLocale: LegalContentLocale = mounted
    ? legalLocaleFor(locale)
    : 'en';
  const localeTag = mounted ? locale : 'en-US';
  const doc = getLegalPage(variants, contentLocale);
  const availableLocales = Object.keys(variants) as LegalContentLocale[];

  if (!doc) return <LegalUnavailable slug={slug} />;

  return (
    <LegalPageLayout
      doc={doc}
      slug={slug}
      contentLocale={contentLocale}
      localeTag={localeTag}
      availableLocales={availableLocales}
    />
  );
};
